import { Stack } from '@mui/material';
import { GridFilterModel } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoaderData, useNavigation } from 'react-router';
import { useNotifications } from '@toolpad/core/useNotifications';
import useNavToEvent, { extractIdFromSelf } from '../../hooks/useNavToEvent';
import { OpportunityQueryData } from '../../loaders/eventsLoaders';
import { AppDispatch } from '../../store';
import { setActiveEventBasicInfo } from '../../store/slices/activeEventSlice';
import { setAllEventsFilterModel } from '../../store/slices/reportControlsSlice';
import { RootState } from '../../../shared/types/appTypes';
import { notifyError } from '../../utils/notifyError';

// Import the new components
import EventFilterControls from './components/EventFilterControls';
import EventsDataGrid from './components/EventsDataGrid';

const AllEvents = () => {
  // Get data from the loader (now returns null - component manages its own loading)
  const loaderData = useLoaderData() as { events: Promise<{ data: { results: OpportunityQueryData[] } }> | null };
  
  const navigation = useNavigation();
  const isNavigationLoading = navigation.state === 'loading';
  const navToEvent = useNavToEvent();
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();

  // Get filter model from Redux with a fallback default value
  const filterModel = useSelector(
    (state: RootState) => state.reportControls?.allEventsFilterModel || { items: [], quickFilterValues: [] }
  );

  // State for events data
  const [eventsData, setEventsData] = useState<OpportunityQueryData[]>([]);
  const [hasControlsChanged, setHasControlsChanged] = useState(false);
  const [isSearching, setIsSearching] = useState(true); // Start as true for initial load

  // Event handlers
  const handleEventsLoaded = (events: OpportunityQueryData[]) => {
    setEventsData(events);
    setIsSearching(false);
  };

  // Handler for filter model change from DataGrid
  const handleFilterModelChange = (newModel: GridFilterModel) => {
    dispatch(setAllEventsFilterModel(newModel));
  };

  // Handler for row clicks
  const handleRowClick = (params: any) => {
    const event = params.row;
    const rawEventId = event?.id || extractIdFromSelf(event?.self);
    const eventId = typeof rawEventId === 'string' ? rawEventId.trim() : '';

    // Defensive: never navigate with an empty/undefined ID. The single-event
    // loader would otherwise either redirect to a stale Redux ID or throw.
    if (!eventId) {
      notifyError(
        notifications,
        'Could not determine event ID for the selected row. Please refresh and try again.',
        'Could not open event.'
      );
      return;
    }

    // Set basic event info in the store immediately. (This now also clears
    // gigs/mainData/dayliteData so the next event never shows stale data.)
    dispatch(
      setActiveEventBasicInfo({
        id: eventId,
        name: event.name || `Event ${eventId}`
      })
    );

    // Navigate to the event page
    try {
      navToEvent(event.self || eventId);
    } catch (err) {
      notifyError(notifications, err, 'Could not open event.');
    }
  };

  // Handler for name search change
  const handleSearchChange = (search: string) => {
    // The EventFilterControls component manages its own search state
    // This callback is here for potential future use or integration
  };

  // Handler for when filter controls change
  const handleControlsChange = (hasChanges: boolean) => {
    setHasControlsChanged(hasChanges);
  };

  // Use eventsData as the single source of truth
  const displayedEvents = eventsData;

  // Determine loading state for DataGrid
  const isLoading = isNavigationLoading || isSearching;

  return (
    <Stack spacing={2}>
      {/* Filter Controls Component */}
      <EventFilterControls
        onEventsLoaded={handleEventsLoaded}
        onSearchChange={handleSearchChange}
        onControlsChange={handleControlsChange}
        hasControlsChanged={hasControlsChanged}
        disableAutoLoad={false}
      />

      {/* Events Data Grid Component */}
      <EventsDataGrid
        events={displayedEvents}
        isLoading={isLoading}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        onRowClick={handleRowClick}
      />
    </Stack>
  );
};

export default AllEvents;
